import { Lightbulb } from "lucide-react";

import { AiPart, PickType } from "@intelligems/ig-types";

import { StepRow } from "./StepRow";

const MAX_LENGTH = 120;

// Keep a thought row short. Native reasoning summaries lead with a one-line
// **bold header** (e.g. "**Considering advice for setup**") followed by a long
// body — use the header, which is the model's own gist. Fall back to the first
// sentence when there's no header.
const summarize = (text: string): string => {
  const trimmed = text.trim();
  if (trimmed.startsWith("**")) {
    const end = trimmed.indexOf("**", 2);
    if (end > 2) {
      const header = trimmed.slice(2, end).trim();
      if (header) return header;
    }
  }
  const firstSentence = (
    trimmed.match(/^.*?[.!?](?=\s|$)/)?.[0] ?? trimmed
  ).trim();
  return firstSentence.length > MAX_LENGTH
    ? `${firstSentence.slice(0, MAX_LENGTH).trimEnd()}…`
    : firstSentence;
};

/**
 * Renders a model reasoning part as a "Thought about this step" timeline row
 * (see the experience-agent thought-process design) instead of the raw
 * streaming reasoning panel / spinner. Placeholder/empty reasoning is dropped so
 * it leaves no row.
 */
export const ReasoningStep = ({
  part,
}: {
  part: PickType<AiPart, "reasoning">;
}) => {
  const text = part.text?.trim();
  if (!text || text === "Thinking..." || text.toLowerCase().includes("noop")) {
    return null;
  }

  return (
    <StepRow
      Icon={Lightbulb}
      title="Thought about this step"
      description={summarize(text)}
    />
  );
};
